import {
  LeistungEigenschaft,
  Produktlinie,
  selectIndicesOfDeactivatedProduktlinien,
  UebergreifendItem,
  ZuersZoneEnum,
  ZusatzoptionItem,
} from '@ps20-frontend/frontend-models';
import { getSelectedProduktlinie } from '@ps20-frontend/preis-lib';
import { Produktlinienname } from '@ps20/bff-models';
import { IAppState } from '../../../store/appState';
import {
  VersicherungssummeEnum,
  VertragsoptionenVHV,
} from '../../../store/model/vertragsoptionen.model';
import {
  ZusatzoptionenNameVHVEnum,
  ZusatzoptionItemVHV,
} from '../../../store/model/zusatzoptionenVHV.model';
import { VersicherungsSummeGrenzen } from '../../../store/model/versicherungssummen';
import {
  FahradwertForLinie,
  ZUSATZOPTION_FAHRRAD_ID,
} from '../../../store/model/fahradwertForLinie.model';
import { selectStaendigBewohntBoolean } from './versObjekt.selector';

export const selectSelectedProduktlinie = (state: IAppState): Produktlinie =>
  getSelectedProduktlinie(state.produkt);

export const isHoechsteZuersZone = (state: IAppState): boolean => {
  return (
    state.elementarSelbstbehalt.id === ZuersZoneEnum.VIER && !state.vorbelegungen?.activateElementar
  );
};

export const selectZusatzoptionItems = (state: IAppState): Array<ZusatzoptionItemVHV> => {
  const selectedProduct: Produktlinie = selectSelectedProduktlinie(state);
  const selectedZusatzoptionen: ZusatzoptionItemVHV[] =
    selectSelectedProductZusatzoptionItems(state);
  const versicherungssummeFahrrad: FahradwertForLinie[] = state?.versicherungssummeFahrrad;

  return selectedZusatzoptionen.map(zusatzoption => {
    if (zusatzoption.id === ZUSATZOPTION_FAHRRAD_ID) {
      const selectedVersSumme = versicherungssummeFahrrad?.find(
        x => x.produktlinie === selectedProduct.produktName,
      )?.summe;
      return {
        ...zusatzoption,
        eigenschaft: selectedVersSumme || '',
      };
    } else {
      return zusatzoption;
    }
  });
};

const selectSelectedProductZusatzoptionItems = (state: IAppState): ZusatzoptionItem[] =>
  getSelectedProduktlinie(state.produkt)?.zusatzoptionen || [];

export const selectProduktlinien = (state: IAppState): Array<Produktlinie> =>
  state.produkt?.produktlinien || [];

export const selectUebergreifendeLeistungen = (state: IAppState): Array<UebergreifendItem> =>
  state.produkt?.uebergreifend || [];

export const selectSelectedZusatzoptionItems = (state: IAppState): Array<ZusatzoptionItem> => {
  return selectSelectedProductZusatzoptionItems(state).filter(elem => elem.isSelected);
};

export const selectShowPremiumNotAllowedZuersZone4 = (state: IAppState): boolean => {
  return isHoechsteZuersZone(state);
};

export const selectShowPremiumNotAllowedVersicherungssumme = (state: IAppState): boolean => {
  const versicherungssummeXL = state.versicherungssummen.find(
    versicherungssumme => versicherungssumme.produktlinie === Produktlinienname.PREMIUM,
  );

  if (!versicherungssummeXL) {
    return false;
  }

  return versicherungssummeXL.summe > VersicherungsSummeGrenzen.MAX_VERSSUMME;
};

export const selectNichtStaendigBewohntUeber50000 = (state: IAppState): boolean => {
  return (
    state.vertragsoptionen?.versicherungssumme >
      VersicherungsSummeGrenzen.MAX_VERSSUMME_NICHT_STAENDIG_BEWOHNT &&
    !selectStaendigBewohntBoolean(state)
  );
};

export const selectStaendigBewohntUeber500000 = (state: IAppState): boolean => {
  return (
    (state.vertragsoptionen?.versicherungssumme > VersicherungsSummeGrenzen.MAX_VERSSUMME ||
      selectShowPremiumNotAllowedVersicherungssumme(state)) &&
    selectStaendigBewohntBoolean(state)
  );
};
export const selectTransientStateWertsachensumme = (state: IAppState): number | undefined => {
  if (
    state.vertragsoptionen?.versicherungssummeArt === VersicherungssummeEnum.BISHERIGE_VERSSUMME
  ) {
    return state?.transientState?.relevantVorbelegungdaten?.wertsachensumme;
  } else {
    const selectedProductline = selectSelectedProduktlinie(state);
    if (state.wertsachensummen && selectedProductline) {
      return state.wertsachensummen.find(
        wertsachensumme => wertsachensumme.produktline === selectedProductline.produktName,
      ).summe;
    } else {
      return undefined;
    }
  }
};

export const selectMaxInsurableSumExceeded = (state: IAppState): boolean => {
  if (!selectStaendigBewohntBoolean(state)) {
    return false;
  }
  const selectedProductline = selectSelectedProduktlinie(state);
  const wertsachensumme = selectTransientStateWertsachensumme(state);
  if (wertsachensumme && selectedProductline) {
    const currentProductLine = state?.wertsachensummen?.find(
      element => element.produktline === selectedProductline.produktName,
    );
    const selectedMaxValue = currentProductLine?.maxSumme;
    return wertsachensumme > selectedMaxValue;
  }
  return false;
};

export const selectNichtStaendigBewohntUnter50000 = (state: IAppState): boolean => {
  return (
    state.vertragsoptionen?.versicherungssumme <=
      VersicherungsSummeGrenzen.MAX_VERSSUMME_NICHT_STAENDIG_BEWOHNT &&
    !selectStaendigBewohntBoolean(state)
  );
};
export const selectVertragsoptionen = (state: IAppState): VertragsoptionenVHV =>
  state.vertragsoptionen;

export const selectVertragsoptionSelectedProduktlinie = (state: IAppState): VertragsoptionenVHV => {
  const ausgewaehlteProduktlinie = selectSelectedProduktlinie(state);

  if (state.versicherungssummen.length === 0 || !ausgewaehlteProduktlinie) {
    return null;
  }

  const versicherungssummeGewaehlteLinie = state.versicherungssummen
    .find(
      versicherungsumme => versicherungsumme.produktlinie === ausgewaehlteProduktlinie.produktName,
    ).summe;

  return {
    ...state.vertragsoptionen,
    versicherungssumme: versicherungssummeGewaehlteLinie,
  };
};

export const selectGesamtbeitrag = (state: IAppState): number => state.gesamtbeitrag;

export const selectSelectedProduktlinieIndex = (state: IAppState): number => {
  return state.produkt?.produktlinien.findIndex(produktlinie => produktlinie.selected);
};

export const selectProduktlinienForWohnung = (state: IAppState): Array<Produktlinie> => {
  const selectedProduktLinien: Array<Produktlinie> = selectProduktlinien(state);
  if (!state.versObjekt.staendigBewohnt) {
    selectedProduktLinien.forEach(produktlinie =>
      produktlinie.leistungen.forEach(leistung => {
        if (
          [
            'BargeldUndAufGeldkartenGeladeneBetraege',
            'bankschliessfach',
            'wertsachenaufbrechen',
          ].includes(leistung.id)
        ) {
          leistung.eigenschaft = LeistungEigenschaft.EXKLUSIVE;
        }
      }),
    );
  }
  return selectedProduktLinien;
};
export const selectVersicherungssummeText = (state: IAppState): string => {
  return selectProduktlinienForWohnung(state)[0]?.leistungen.find(
    lst => lst.id === 'versicherungssumme',
  )?.info;
};

export const selectProduktlinieIndexesToDeactivate = (state: IAppState): number[] => {
  const indexesOfDeactivatedProduktlinien = new Set(selectIndicesOfDeactivatedProduktlinien(state));
  const produktlinien = selectProduktlinienForWohnung(state);
  const maxVersicherungssumeForPremiumProduktLinie = 500000;
  const premiumProduktlinie = state.versicherungssummen.find(
    versicherungssume => versicherungssume.produktlinie === Produktlinienname.PREMIUM,
  );

  if (premiumProduktlinie?.summe > maxVersicherungssumeForPremiumProduktLinie) {
    indexesOfDeactivatedProduktlinien.add(
      produktlinien.findIndex(produklinie => produklinie.produktName === Produktlinienname.PREMIUM),
    );
  }
  return [...indexesOfDeactivatedProduktlinien];
};

export const selectElementarbaustein = (state: IAppState): ZusatzoptionItemVHV => {
  return selectZusatzoptionItems(state).find(
    item => item.name === ZusatzoptionenNameVHVEnum.ELEMENTAR,
  );
};
